import React, { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { 
  Users, 
  Calendar,
  Building2,
  RefreshCw,
  CheckSquare,
  Square,
  Wrench,
  MapPin,
  UserPlus
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// Status colors
const STATUS_STYLES = {
  "Compliant": "bg-green-100 text-green-700",
  "Due Soon": "bg-amber-100 text-amber-700",
  "Overdue": "bg-red-100 text-red-700",
  "Failed": "bg-red-200 text-red-800",
};

export default function PPMBulkAssignPage() {
  const [schedules, setSchedules] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [selected, setSelected] = useState([]);
  const [selectedContractorId, setSelectedContractorId] = useState("");
  const [statusFilter, setStatusFilter] = useState("Due Soon");
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(false);

  const loadData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };
      
      const [schedulesRes, contractorsRes] = await Promise.all([
        axios.get(`${API}/api/ppm/schedules?status=${statusFilter}&limit=200`, { headers }),
        axios.get(`${API}/api/contractors`, { headers }),
      ]);
      
      setSchedules(schedulesRes.data.rows || []);
      setContractors(contractorsRes.data || []);
    } catch (err) {
      console.error("Failed to load data:", err);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [statusFilter]);

  const toggleSelect = (scheduleId) => {
    setSelected(prev => 
      prev.includes(scheduleId) 
        ? prev.filter(id => id !== scheduleId)
        : [...prev, scheduleId]
    );
  };

  const selectAll = () => {
    if (selected.length === schedules.length) {
      setSelected([]);
    } else {
      setSelected(schedules.map(s => s.schedule_id));
    }
  };

  const handleBulkAssign = async () => {
    if (!selectedContractorId) {
      toast.error("Please select a contractor");
      return;
    }
    if (selected.length === 0) {
      toast.error("Please select at least one schedule");
      return;
    }

    try {
      setAssigning(true);
      const token = localStorage.getItem("token");
      
      const res = await axios.post(`${API}/api/ppm/schedule/bulk-assign`, {
        schedule_ids: selected,
        contractor_id: selectedContractorId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(`Assigned ${res.data.schedules_updated} schedules to contractor`);
      if (res.data.jobs_updated > 0) {
        toast.info(`Also updated ${res.data.jobs_updated} active jobs`);
      }
      
      setSelected([]);
      loadData();
    } catch (err) {
      console.error("Failed to assign:", err);
      toast.error(err.response?.data?.detail || "Failed to assign contractor");
    } finally {
      setAssigning(false);
    }
  };

  // Group schedules by park
  const parkGroups = schedules.reduce((acc, schedule) => {
    const park = schedule.park_name || "Unknown Park";
    if (!acc[park]) acc[park] = [];
    acc[park].push(schedule);
    return acc;
  }, {});

  // Get unique assigned contractor names for summary
  const unassignedCount = schedules.filter(s => !s.assigned_contractor_id).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 bg-slate-50 min-h-screen" data-testid="ppm-bulk-assign-page">
      {/* Header */}
      <div className="rounded-2xl bg-gradient-to-r from-teal-700 to-teal-600 p-6 text-white">
        <h1 className="text-2xl font-bold" data-testid="page-title">PPM Bulk Assign</h1>
        <p className="text-teal-100 text-sm mt-1">Assign one contractor to multiple PPM schedules at once</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-teal-500" data-testid="total-card">
          <CardContent className="p-4">
            <div className="text-sm text-slate-500">Total Schedules</div>
            <div className="text-3xl font-bold text-teal-700">{schedules.length}</div>
          </CardContent>
        </Card>
        
        <Card className="border-l-4 border-l-blue-500" data-testid="selected-card">
          <CardContent className="p-4">
            <div className="text-sm text-slate-500">Selected</div>
            <div className="text-3xl font-bold text-blue-600">{selected.length}</div>
          </CardContent>
        </Card>
        
        <Card className="border-l-4 border-l-red-500" data-testid="unassigned-card">
          <CardContent className="p-4">
            <div className="text-sm text-slate-500">Unassigned</div>
            <div className="text-3xl font-bold text-red-600">{unassignedCount}</div>
          </CardContent>
        </Card>
        
        <Card className="border-l-4 border-l-purple-500" data-testid="parks-card">
          <CardContent className="p-4">
            <div className="text-sm text-slate-500">Parks</div>
            <div className="text-3xl font-bold text-purple-600">{Object.keys(parkGroups).length}</div>
          </CardContent>
        </Card>
      </div>

      {/* Action Bar */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            {/* Status Filter */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-600">Status:</span>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40" data-testid="status-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Due Soon">Due Soon</SelectItem>
                  <SelectItem value="Overdue">Overdue</SelectItem>
                  <SelectItem value="Compliant">Compliant</SelectItem>
                  <SelectItem value="Failed">Failed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="h-6 w-px bg-slate-200" />

            {/* Contractor Selector */}
            <div className="flex items-center gap-2 flex-1 min-w-[250px]">
              <Users className="h-4 w-4 text-slate-500" />
              <Select value={selectedContractorId} onValueChange={setSelectedContractorId}>
                <SelectTrigger className="flex-1" data-testid="contractor-selector">
                  <SelectValue placeholder="Select Contractor to Assign" />
                </SelectTrigger>
                <SelectContent>
                  {contractors.map(c => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.company_name || c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Assign Button */}
            <Button 
              onClick={handleBulkAssign}
              disabled={selected.length === 0 || !selectedContractorId || assigning}
              className="bg-teal-600 hover:bg-teal-700"
              data-testid="assign-btn"
            >
              {assigning ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <UserPlus className="h-4 w-4 mr-2" />
              )}
              Assign {selected.length > 0 ? `(${selected.length})` : ""}
            </Button>

            {/* Refresh */}
            <Button variant="ghost" size="sm" onClick={loadData} data-testid="refresh-btn">
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Schedules List */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">PPM Schedules ({schedules.length})</CardTitle>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={selectAll}
              data-testid="select-all-btn"
            >
              {selected.length === schedules.length ? (
                <>
                  <CheckSquare className="h-4 w-4 mr-2" />
                  Deselect All
                </>
              ) : (
                <>
                  <Square className="h-4 w-4 mr-2" />
                  Select All
                </>
              )}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {schedules.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              <Calendar className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>No schedules found with status "{statusFilter}"</p>
            </div>
          ) : (
            <div className="space-y-4 max-h-[600px] overflow-auto">
              {Object.entries(parkGroups).map(([park, items]) => (
                <div key={park} className="border rounded-lg overflow-hidden">
                  {/* Park Header */}
                  <div className="flex items-center gap-2 p-3 bg-slate-50 border-b">
                    <Building2 className="h-4 w-4 text-slate-500" />
                    <span className="font-medium">{park}</span>
                    <Badge variant="secondary">{items.length} items</Badge>
                  </div>
                  
                  {/* Schedule Items */}
                  <div className="divide-y">
                    {items.map((schedule) => (
                      <label 
                        key={schedule.schedule_id} 
                        className={`flex items-start gap-4 p-4 cursor-pointer transition-colors ${
                          selected.includes(schedule.schedule_id) ? 'bg-teal-50' : 'hover:bg-slate-50'
                        }`}
                        data-testid={`schedule-item-${schedule.schedule_id}`}
                      >
                        <Checkbox
                          checked={selected.includes(schedule.schedule_id)}
                          onCheckedChange={() => toggleSelect(schedule.schedule_id)}
                          className="mt-1"
                        />
                        
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <MapPin className="h-3 w-3 text-slate-400" />
                            <span className="font-medium text-sm">{schedule.unit_name}</span>
                            {schedule.asset_ref && (
                              <span className="text-xs text-slate-400">({schedule.asset_ref})</span>
                            )}
                          </div>
                          
                          <div className="flex items-center gap-2 text-sm">
                            <Wrench className="h-3 w-3 text-slate-400" />
                            <span>{schedule.template_name}</span>
                            <Badge className={STATUS_STYLES[schedule.status]}>{schedule.status}</Badge>
                          </div>
                          
                          <div className="flex items-center gap-4 text-xs text-slate-500 mt-2">
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              Due: {schedule.next_due_date 
                                ? new Date(schedule.next_due_date).toLocaleDateString() 
                                : "-"}
                            </span>
                            <span>Trade: {schedule.trade_type}</span>
                            <span>
                              Every {schedule.frequency_value} {schedule.frequency_unit}
                            </span>
                          </div>
                        </div>
                        
                        <div className="text-right text-sm">
                          {schedule.assigned_contractor_name ? (
                            <span className="text-slate-600">{schedule.assigned_contractor_name}</span>
                          ) : (
                            <Badge className="bg-red-100 text-red-700">Unassigned</Badge>
                          )}
                          {schedule.active_job_reference && (
                            <div className="text-xs text-teal-600 mt-1">{schedule.active_job_reference}</div>
                          )}
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
